/**
 * State machine for changing the email address on an UNVERIFIED account.
 *
 * Rescues the user who typed `@fmail.com`. Before this, both actions on the
 * verification notice were dead ends for her: Resend mails the same wrong
 * address again, and Check can never turn true.
 *
 * `verifyBeforeUpdateEmail`, NEVER `updateEmail`. Firebase mails the
 * confirmation link to the NEW address and commits the change only when that
 * link is opened. Two consequences, both load-bearing:
 *   - The old address keeps working until she clicks. Abandoning costs nothing,
 *     and her boards are untouched.
 *   - It CANNOT move an account onto an address the user does not control,
 *     which is the account-takeover hole a plain `updateEmail` would open.
 * (`updateEmail` is not even available to us — `enableImprovedEmailPrivacy` is
 * on, see v4Auth.ts:227 — but the security property above is the real reason.)
 *
 * SCOPE, HELD DELIBERATELY TIGHT. This only ever runs for
 * `emailVerified === false`. Changing a VERIFIED address is the classic
 * account-takeover lever and is a different feature with a different bar. That
 * boundary is not enforced here by a second check that could drift: the sole
 * host, EmailVerificationNotice, stops rendering the instant the address is
 * confirmed, so the flow is unreachable rather than merely guarded.
 *
 * STALE SESSIONS. `verifyBeforeUpdateEmail` throws
 * `auth/requires-recent-login` on an older session. Rather than invent a second
 * vocabulary for it, this reuses auth/reauth.ts — the same primitive
 * `useDeleteAccount` maps onto its 'needs-reauth' state. The pending address
 * survives the detour, so re-authenticating resumes the send instead of making
 * her retype what she already typed.
 */

import { useCallback, useMemo, useState } from 'react';
import { verifyBeforeUpdateEmail, type User } from 'firebase/auth';

import { verificationActionSettings } from './emailVerification';
import {
  hasPasswordProvider,
  isRequiresRecentLogin,
  mapReauthError,
  reauthenticateWithPassword,
} from './reauth';

export type ChangeEmailStep =
  /** Closed. The notice shows its normal verify/resend content. */
  | 'idle'
  /** The inline form, asking for the new address. */
  | 'form'
  /** Session too old: asking for the password before we send. */
  | 'reauth'
  /** Link sent to the new address. Nothing has changed yet. */
  | 'sent';

export type ChangeEmailError =
  /** Not a usable address. Caught locally and by Firebase. */
  | 'invalid-email'
  /** Same address they already have — sending would achieve nothing. */
  | 'same-email'
  /** Firebase says that address already belongs to an account. */
  | 'in-use'
  /** Per-account throttle on verification mail. */
  | 'too-many'
  /** Re-authentication failed on the password itself. */
  | 'wrong-password'
  /** Stale session on an account with no password to re-enter. */
  | 'no-password'
  /** Offline, or Firebase refused the operation outright. */
  | 'unavailable'
  /** Anything else. */
  | 'unknown';

export interface ChangeEmailState {
  step: ChangeEmailStep;
  /** The address we asked Firebase to confirm. Survives the reauth detour. */
  pendingEmail: string | null;
  error: ChangeEmailError | null;
  /** True while a Firebase call is in flight; drives button disabling. */
  busy: boolean;
}

const INITIAL: ChangeEmailState = {
  step: 'idle',
  pendingEmail: null,
  error: null,
  busy: false,
};

/**
 * Local pre-check, intentionally permissive.
 *
 * Firebase is the authority on what it will accept; this only rejects what is
 * obviously not an address, so we do not spend a network round trip — and a
 * throttle slot — on `a@`. Anything arguable is allowed through to Firebase
 * rather than blocked by a regex that would eventually be wrong about someone's
 * real address.
 */
function looksLikeEmail(value: string): boolean {
  const at = value.indexOf('@');
  if (at <= 0) return false;
  if (value.indexOf('@', at + 1) !== -1) return false;
  const domain = value.slice(at + 1);
  return domain.includes('.') && !domain.startsWith('.') && !domain.endsWith('.');
}

function mapSendError(err: unknown): ChangeEmailError {
  const code = typeof err === 'object' && err !== null && typeof (err as { code?: unknown }).code === 'string'
    ? (err as { code: string }).code
    : '';
  if (code.includes('invalid-email') || code.includes('missing-email')) return 'invalid-email';
  if (code.includes('email-already-in-use')) return 'in-use';
  if (code.includes('too-many-requests')) return 'too-many';
  if (code.includes('network-request-failed') || code.includes('operation-not-allowed')) {
    return 'unavailable';
  }
  return 'unknown';
}

export interface UseChangeEmail extends ChangeEmailState {
  /** Open the inline form. No Firebase call yet. */
  begin: () => void;
  /** Close and reset, from any step. */
  cancel: () => void;
  /** Validate and mail the confirmation link to `email`. */
  submit: (email: string) => Promise<void>;
  /** Re-authenticate, then resume the send that triggered the prompt. */
  confirmReauth: (password: string) => Promise<void>;
  /** From the sent state, go back to the form to try another address. */
  changeAgain: () => void;
}

export function useChangeEmail(user: User | null): UseChangeEmail {
  const [state, setState] = useState<ChangeEmailState>(INITIAL);

  const begin = useCallback(() => setState({ ...INITIAL, step: 'form' }), []);
  const cancel = useCallback(() => setState(INITIAL), []);
  // Keeps `pendingEmail` so the form can prefill the address she just sent to —
  // "use a different address" usually means editing that one, not retyping it.
  const changeAgain = useCallback(
    () => setState((prev) => ({ ...prev, step: 'form', error: null })),
    [],
  );

  /**
   * The one place the Firebase call is made. Both `submit` and the post-reauth
   * resume funnel through here so the two paths cannot drift on error mapping,
   * on the action-code settings, or on what "sent" means.
   */
  const send = useCallback(
    async (target: User, email: string): Promise<void> => {
      try {
        await verifyBeforeUpdateEmail(target, email, verificationActionSettings);
        setState({ step: 'sent', pendingEmail: email, error: null, busy: false });
      } catch (err) {
        if (isRequiresRecentLogin(err)) {
          // An account with no password cannot answer a password prompt, so do
          // not show one — say what will actually work instead.
          if (!hasPasswordProvider(target)) {
            setState({ step: 'form', pendingEmail: email, error: 'no-password', busy: false });
            return;
          }
          setState({ step: 'reauth', pendingEmail: email, error: null, busy: false });
          return;
        }
        setState({ step: 'form', pendingEmail: email, error: mapSendError(err), busy: false });
      }
    },
    [],
  );

  const submit = useCallback(
    async (email: string) => {
      const next = email.trim();
      if (!user || !next) return;
      if (!looksLikeEmail(next)) {
        setState((s) => ({ ...s, step: 'form', error: 'invalid-email', busy: false }));
        return;
      }
      // Case-insensitive: Firebase treats addresses case-insensitively, so
      // "Me@x.com" is not a change from "me@x.com" and must not claim to be.
      if (next.toLowerCase() === (user.email ?? '').toLowerCase()) {
        setState((s) => ({ ...s, step: 'form', error: 'same-email', busy: false }));
        return;
      }
      setState((s) => ({ ...s, step: 'form', pendingEmail: next, error: null, busy: true }));
      await send(user, next);
    },
    [user, send],
  );

  const confirmReauth = useCallback(
    async (password: string) => {
      const email = state.pendingEmail;
      if (!user || !password || !email) return;
      setState((s) => ({ ...s, busy: true, error: null }));
      try {
        await reauthenticateWithPassword(user, password);
      } catch (err) {
        const mapped = mapReauthError(err);
        setState((s) => ({
          ...s,
          busy: false,
          error: mapped,
          // A wrong password is fixable by retyping, so stay on the prompt.
          // Anything else is not, so fall back to the form she came from.
          step: mapped === 'wrong-password' || mapped === 'too-many' ? 'reauth' : 'form',
        }));
        return;
      }
      // Session is fresh again — resume the send she already asked for.
      await send(user, email);
    },
    [user, state.pendingEmail, send],
  );

  return useMemo(
    () => ({ ...state, begin, cancel, submit, confirmReauth, changeAgain }),
    [state, begin, cancel, submit, confirmReauth, changeAgain],
  );
}
